import { useMemo, useState } from 'react'
import { Box, Button, Stack } from '@mui/material'
import { EmptyState } from '@ui2/feedback'
import { Section } from '@ui2/structure'
import { LoadingIndicator } from '@ui/feedback'
import { SectionHeader } from '@ui/structure'
import { PDFAConformance } from '@editorComponents'
import { groupPlansByDate, planDateFormatter } from '../../utils'
import AddPlanDialog from '../AddPlanDialog'
import PlanRow from './components/PlanRow'

interface Props {
  selectedPlan: string | null
  setSelectedPlan: (plan: string | null) => void
  onAddPlan: (plan: Plan) => void
  onDeletePlan: (plan: Plan) => void
  plans: Plan[]
  isLoadingPlans: boolean
  getBlobPDFA: (
    pdfUlr: string,
    conformance: PDFAConformance,
  ) => Promise<{ blob: Blob; errors: string[] } | null>
}

const PlansSection = ({
  selectedPlan,
  setSelectedPlan,
  onAddPlan,
  onDeletePlan,
  plans,
  isLoadingPlans,
  getBlobPDFA,
}: Props) => {
  const [newPlanDialogOpen, setNewPlanDialogOpen] = useState(false)

  const plansByDate = useMemo(() => groupPlansByDate(plans), [plans])

  return (
    <Stack direction="column" spacing={1} sx={{ height: '100%' }}>
      <Box>
        <SectionHeader>Gespeicherte Pläne</SectionHeader>
      </Box>
      <Stack direction="column" spacing={1} pt={1} pb={1} pl={1} pr={1} sx={{ overflowY: 'auto' }}>
        {isLoadingPlans ? (
          <Box height={150}>
            <LoadingIndicator />
          </Box>
        ) : plansByDate.length === 0 ? (
          <EmptyState>Keine gespeicherten Pläne vorhanden</EmptyState>
        ) : (
          plansByDate.map(([date, datePlans]) => (
            <Section key={date} title={planDateFormatter.format(new Date(date))} collapsible>
              {datePlans.map(plan => (
                <PlanRow
                  key={plan.guid}
                  plan={plan}
                  selected={plan.guid === selectedPlan}
                  onSelect={() => setSelectedPlan(plan.guid)}
                  onDelete={() => onDeletePlan(plan)}
                  onDuplicate={onAddPlan}
                  getBlobPDFA={getBlobPDFA}
                />
              ))}
            </Section>
          ))
        )}

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => setNewPlanDialogOpen(true)}
            disabled={isLoadingPlans}
          >
            Plan hinzufügen
          </Button>
        </Stack>
      </Stack>

      <AddPlanDialog
        open={newPlanDialogOpen}
        onClose={() => setNewPlanDialogOpen(false)}
        onAddPlan={plan => {
          setNewPlanDialogOpen(false)
          onAddPlan(plan)
        }}
      />
    </Stack>
  )
}

export default PlansSection
